import React, { SyntheticEvent } from "react";
import {
  Col,
  Collapse,
  Form,
  Input,
  Row,
  Space,
  Typography,
  Divider,
  Checkbox,
  Avatar,
  Upload,
  FormInstance,
} from "antd";
import { FileImageOutlined } from "@ant-design/icons";
import DocIcon from "../../Atoms/images/file-icon/doc_icon.png";
import PdfIcon from "../../Atoms/images/file-icon/pdf_icon.png";
import XlsIcon from "../../Atoms/images/file-icon/xls_icon.png";
import styled from "@emotion/styled";
import { ConformityCertificate } from "./conformity-certificate";
import { NamePath } from "antd/lib/form/interface";

const { Panel } = Collapse;
const StyledSpace = styled(Space)`
  width: 100%;
`;
const StyledRow = styled(Row)`
  margin-bottom: 32px;
`;
export interface File {
  fileName: string;
  url?: string;
  size: number;
  mimetype: string;
  uuid: string;
}

interface ReviewerComplianceCheckProps {
  title: string;
  readOnly?: boolean;
  remarks: string;
  name?: string;
  files?: Array<any>;
  documentLabel?: string;
  remarkLabel?: string;
  reviewerRemarksName?: string | string[];
  reUploadName?: string | string[];
  conformityCertificateName?: string;
  type?: "images" | "document" | "";
  isConformityCertificate?: boolean;
  hiddenRequireReupload?: boolean;
  hiddenFiles?: boolean;
  defaultTestLines?: Array<any>;
  onAddTestLine?: (conformityType: string) => void;
  onRemoveTestLine?: (idx: number, conformityType: string) => void;
  onEditTestLine?: (
    record: Record<string, any>,
    index: number,
    conformityType: string
  ) => void;
  onViewCertificate?: () => void;
  renderFileSection?: () => React.ReactNode;
  onEditGeneralInformation?: (conformityType: string, values: any) => void;
  onClickGeneratePDF?: (conformityType: string) => void;
  product?: any;
  compliance?: any;
  form: FormInstance;
}

const renderFile = (
  originNode: any,
  file: any,
  fileList: object[],
  actions: any
) => {
  const patternFileExtension = /\.([0-9a-z]+)(?:[#]|$)/i;
  const extension =
    (file.fileName
      ? file.fileName.match(patternFileExtension)
      : file.name.match(patternFileExtension)) || [];

  let icon = null;
  switch (extension[1]) {
    case "doc":
    case "docx":
      icon = DocIcon;
      break;
    case "pdf":
      icon = PdfIcon;
      break;
    case "xls":
    case "xlsx":
      icon = XlsIcon;
      break;
    default:
      icon = <FileImageOutlined style={{ color: "#F98001", fontSize: 22 }} />;
  }

  return (
    <Row align="middle">
      <Col flex={"none"}>
        <Avatar
          shape="square"
          size="small"
          style={{ margin: "16px 10px 5px 0px" }}
          src={icon}
        />
      </Col>
      <Col flex={`none`}>
        <div
          style={{ marginTop: 12, cursor: "pointer" }}
          // onClick={() => actions.download()}
        >
          {file.name || file.fileName}
        </div>
      </Col>
    </Row>
  );
};

export function ReviewerComplianceCheck(props: ReviewerComplianceCheckProps) {
  return (
    <Collapse
      ghost
      expandIconPosition={`right`}
      style={{ width: "100%", padding: 0 }}
    >
      <Panel header={<h2>{props.title}</h2>} key="1">
        {!props.hiddenFiles && (
          <StyledRow>
            <Space direction="vertical">
              <h3>{props.documentLabel || "Images"}</h3>
              <Row>
                {props.renderFileSection ? (
                  props.renderFileSection()
                ) : (
                  <>
                    {props.files?.length ? (
                      <Upload
                        showUploadList={{ showRemoveIcon: false }}
                        fileList={props.files}
                        listType={
                          props.type === "document" ? "picture" : "picture-card"
                        }
                        itemRender={
                          props.type === "document" ? renderFile : undefined
                        }
                      ></Upload>
                    ) : (
                      "N/A"
                    )}
                  </>
                )}
              </Row>
            </Space>
          </StyledRow>
        )}

        <Row>
          <Space direction="vertical">
            <h3>{props.remarkLabel || "Remarks"}</h3>
            <Typography.Paragraph>{props.remarks}</Typography.Paragraph>
          </Space>
        </Row>
        <Divider></Divider>
        <h2>Review Section</h2>
        {props.isConformityCertificate ? (
          <StyledSpace direction="vertical" size={24}>
            <ConformityCertificate
              disabled={props.readOnly}
              conformityCertificateName={props.conformityCertificateName}
              statusName={"conformityCertificateSelection"}
              testLinesName={"testLines"}
              defaultTestLines={props.defaultTestLines}
              onAddTestLine={props.onAddTestLine}
              onRemoveTestLine={props.onRemoveTestLine}
              onEditTestLine={props.onEditTestLine}
              onEditGeneralInformation={props.onEditGeneralInformation}
              onClickGenerate={props.onClickGeneratePDF}
              product={props.product}
              compliance={props.compliance}
              form={props.form}
            ></ConformityCertificate>
            <Col lg={12}>
              <Form.Item
                label="Reviewer Remarks"
                name={props.reviewerRemarksName}
              >
                <Input.TextArea
                  disabled={props.readOnly}
                  autoSize={{ minRows: 5, maxRows: 5 }}
                />
              </Form.Item>
            </Col>
          </StyledSpace>
        ) : (
          <Row>
            <Col lg={12}>
              <Form.Item
                label="Reviewer Remarks"
                name={props.reviewerRemarksName}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (
                        !value &&
                        !!getFieldValue(props.reUploadName as NamePath)
                      ) {
                        return Promise.reject(
                          new Error("This field is required")
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input.TextArea
                  disabled={props.readOnly}
                  autoSize={{ minRows: 5, maxRows: 5 }}
                />
              </Form.Item>
              {!props.hiddenRequireReupload && (
                <Form.Item
                  label="Reviewing result"
                  name={props.reUploadName}
                  valuePropName="checked"
                >
                  <Checkbox disabled={props.readOnly}>
                    Require re-upload
                  </Checkbox>
                </Form.Item>
              )}
            </Col>
          </Row>
        )}
      </Panel>
    </Collapse>
  );
}

const RequireInfo = styled.div`
  color: #ff5555;
  font-size: 12px;
`;
